import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Upload, Loader2, ImageOff, Save, Wand2, PackagePlus, Camera, FileText, ScanBarcode, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { Header } from "@/components/Header";
import { api, resolveImage } from "@/lib/api";
import { gradeFromScore } from "@/lib/rating";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

const Drop = ({ label, icon: Icon, file, onPick, testid }) => (
  <label className="flex-1 cursor-pointer">
    <input type="file" accept="image/*" hidden data-testid={testid} onChange={(e) => onPick(e.target.files?.[0])} />
    <div className="h-44 rounded-2xl border-2 border-dashed border-slate-200 hover:border-emerald-400 transition-colors flex flex-col items-center justify-center gap-2 overflow-hidden bg-slate-50">
      {file ? <img src={URL.createObjectURL(file)} alt="" className="w-full h-full object-contain" />
        : <><Icon className="w-8 h-8 text-slate-300" /><span className="text-sm text-slate-400">{label}</span></>}
    </div>
  </label>
);

export default function DataPanel() {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [front, setFront] = useState(null);
  const [ingredients, setIngredients] = useState(null);
  const [barcodeImg, setBarcodeImg] = useState(null);
  const [nutritionImg, setNutritionImg] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [draft, setDraft] = useState(null);
  const [score, setScore] = useState(null);
  const [saving, setSaving] = useState(false);
  const [list, setList] = useState([]);

  const loadList = useCallback(() => {
    api.get("/products/search").then((r) => setList(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (loading) return;
    const allowed = user && user.role === "admin" && (user.is_super || user.permissions?.ai_upload);
    if (!allowed) { navigate(user?.role === "admin" ? "/admin" : "/auth"); return; }
    loadList();
  }, [loading, user]);

const compressImage = (file, maxDim = 1600, quality = 0.8) => {
  return new Promise((resolve) => {
    if (!file || !file.type || !file.type.startsWith("image/")) return resolve(file);
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        if (width > height) {
          height = Math.round((height * maxDim) / width);
          width = maxDim;
        } else {
          width = Math.round((width * maxDim) / height);
          height = maxDim;
        }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          resolve(blob ? new File([blob], file.name, { type: "image/jpeg" }) : file);
        },
        "image/jpeg",
        quality
      );
    };
    img.onerror = () => resolve(file);
    img.src = url;
  });
};


  const extract = async () => {
    if (!front) { toast.error(t("data.toast.frontRequired")); return; }
    setExtracting(true);
    try {
      const [cFront, cIngredients, cBarcodeImg, cNutritionImg] = await Promise.all([
        compressImage(front),
        ingredients ? compressImage(ingredients) : Promise.resolve(null),
        barcodeImg ? compressImage(barcodeImg) : Promise.resolve(null),
        nutritionImg ? compressImage(nutritionImg) : Promise.resolve(null),
      ]);
      const fd = new FormData();
      fd.append("front", cFront);
      if (cIngredients) fd.append("ingredients", cIngredients);
      if (cBarcodeImg) fd.append("barcode_img", cBarcodeImg);
      if (cNutritionImg) fd.append("nutrition_img", cNutritionImg);
      const res = await api.post("/admin/ai-extract", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
      setDraft({ ...res.data.draft, e_additives: (res.data.draft.e_additives || []).join(", "), allergens: (res.data.draft.allergens || []).join(", ") });
      setScore(res.data.score);
      toast.success(t("data.toast.aiFilled"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("data.toast.extractFail"));
    } finally {
      setExtracting(false);
    }
  };

  const upd = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const updCert = (k, v) => setDraft((d) => ({ ...d, certificates: { ...d.certificates, [k]: v } }));

  const save = async () => {
    if (!draft.barcode || !draft.name) { toast.error(t("data.toast.barcodeNameReq")); return; }
    setSaving(true);
    const payload = {
      ...draft,
      e_additives: draft.e_additives.split(",").map((s) => s.trim()).filter(Boolean),
      allergens: draft.allergens.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      await api.post("/admin/products", payload);
      toast.success(t("data.toast.savedDb"));
      setDraft(null); setScore(null); setFront(null); setIngredients(null); setBarcodeImg(null); setNutritionImg(null);
      loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("data.toast.saveFail"));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !user) return null;
  const g = score ? gradeFromScore(score.total) : null;

  return (
    <div className="App">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-2 mb-1">
          <span className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center"><Sparkles className="w-5 h-5 text-emerald-600" /></span>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold">{t("data.title")}</h1>
        </div>
        <p className="text-muted-foreground mb-6">{t("data.subtitle")}</p>

        <div className="bg-card rounded-3xl border border-border p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Drop label={t("data.dropFront")} icon={Camera} file={front} onPick={setFront} testid="data-front-upload" />
            <Drop label={t("data.dropIngredients")} icon={FileText} file={ingredients} onPick={setIngredients} testid="data-ingredients-upload" />
            <Drop label={t("data.dropBarcode")} icon={ScanBarcode} file={barcodeImg} onPick={setBarcodeImg} testid="data-barcode-upload" />
            <Drop label={t("data.dropNutrition")} icon={ClipboardList} file={nutritionImg} onPick={setNutritionImg} testid="data-nutrition-upload" />
          </div>
          <Button onClick={extract} disabled={extracting || !front} data-testid="ai-extract-button"
            className="w-full mt-4 h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 font-semibold">
            {extracting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("data.extracting")}</> : <><Wand2 className="w-4 h-4 mr-2" /> {t("data.extract")}</>}
          </Button>
        </div>

        {draft && (
          <div className="bg-card rounded-3xl border border-border p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] mt-6" data-testid="ai-draft-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold">{t("data.cardTitle")}</h2>
              {g && <span className="px-3 py-1.5 rounded-full text-sm font-bold" style={{ background: g.bg, color: g.text }}>{score.total}/100 · {t(`grade.${g.key}`)}</span>}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 flex items-center gap-4">
                <div className="w-20 h-20 rounded-xl bg-slate-50 border border-border overflow-hidden flex items-center justify-center shrink-0">
                  {draft.image_url ? <img src={resolveImage(draft.image_url)} alt="" className="w-full h-full object-contain" /> : <ImageOff className="w-7 h-7 text-slate-300" />}
                </div>
                <span className="text-sm text-muted-foreground">{t("data.photoSaved")}</span>
              </div>
              <F label={t("data.barcode")}><Input value={draft.barcode} onChange={(e) => upd("barcode", e.target.value)} data-testid="draft-barcode" className="rounded-xl" /></F>
              <F label={t("data.name")}><Input value={draft.name} onChange={(e) => upd("name", e.target.value)} data-testid="draft-name" className="rounded-xl" /></F>
              <F label={t("data.brand")}><Input value={draft.brand} onChange={(e) => upd("brand", e.target.value)} className="rounded-xl" /></F>
              <F label={t("data.category")}><Input value={draft.category} onChange={(e) => upd("category", e.target.value)} className="rounded-xl" /></F>
              <div className="sm:col-span-2">
                <F label={t("data.ingredients")}><Textarea value={draft.ingredients_text} onChange={(e) => upd("ingredients_text", e.target.value)} className="rounded-xl min-h-[70px]" /></F>
              </div>
              <F label={t("data.additives")}><Input value={draft.e_additives} onChange={(e) => upd("e_additives", e.target.value)} className="rounded-xl" /></F>
              <F label={t("data.allergens")}><Input value={draft.allergens} onChange={(e) => upd("allergens", e.target.value)} className="rounded-xl" /></F>
              <div className="sm:col-span-2 mt-2 pt-2 border-t border-border" data-testid="nutrition-section">
                <h3 className="font-heading font-bold text-sm text-slate-700">{t("data.nutritionTitle")} <span className="font-normal text-muted-foreground">{t("data.per100")}</span></h3>
              </div>
              <F label={t("data.energy")}><Input type="number" value={draft.energy_kcal ?? ""} onChange={(e) => upd("energy_kcal", e.target.value === "" ? null : parseFloat(e.target.value))} data-testid="draft-energy" className="rounded-xl" /></F>
              <F label={t("data.protein")}><Input type="number" value={draft.protein_g ?? ""} onChange={(e) => upd("protein_g", e.target.value === "" ? null : parseFloat(e.target.value))} data-testid="draft-protein" className="rounded-xl" /></F>
              <F label={t("data.carbs")}><Input type="number" value={draft.carbohydrates_g ?? ""} onChange={(e) => upd("carbohydrates_g", e.target.value === "" ? null : parseFloat(e.target.value))} data-testid="draft-carbs" className="rounded-xl" /></F>
              <F label={t("data.sugar")}><Input type="number" value={draft.sugars_g ?? ""} onChange={(e) => upd("sugars_g", e.target.value === "" ? null : parseFloat(e.target.value))} className="rounded-xl" /></F>
              <F label={t("data.salt")}><Input type="number" value={draft.salt_g ?? ""} onChange={(e) => upd("salt_g", e.target.value === "" ? null : parseFloat(e.target.value))} className="rounded-xl" /></F>
              <F label={t("data.satfat")}><Input type="number" value={draft.saturated_fat_g ?? ""} onChange={(e) => upd("saturated_fat_g", e.target.value === "" ? null : parseFloat(e.target.value))} className="rounded-xl" /></F>
              <F label={t("data.fiber")}><Input type="number" value={draft.fiber_g ?? ""} onChange={(e) => upd("fiber_g", e.target.value === "" ? null : parseFloat(e.target.value))} className="rounded-xl" /></F>
              <div className="sm:col-span-2 flex flex-wrap gap-6">
                <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={draft.trans_fat} onCheckedChange={(v) => upd("trans_fat", !!v)} /> {t("data.transfat")}</label>
                <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={draft.palm_oil} onCheckedChange={(v) => upd("palm_oil", !!v)} /> {t("data.palmoil")}</label>
              </div>
              <div className="sm:col-span-2">
                <Label className="mb-2 block">{t("data.certs")}</Label>
                <div className="flex flex-wrap gap-4">
                  {["halal", "organic", "gost", "eco"].map((k) => (
                    <label key={k} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox checked={draft.certificates[k]} onCheckedChange={(v) => updCert(k, !!v)} /> {t(`data.cert.${k}`)}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <Button onClick={save} disabled={saving} data-testid="save-draft-button" className="w-full mt-5 h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 font-semibold">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} {t("data.saveDb")}
            </Button>
          </div>
        )}

        <div className="mt-8">
          <div className="flex items-center gap-2 mb-3">
            <PackagePlus className="w-5 h-5 text-emerald-600" />
            <h2 className="font-heading text-xl font-bold">{t("data.loadedTitle")}</h2>
            <span className="text-sm text-muted-foreground">({list.length})</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {list.map((p) => {
              const gg = gradeFromScore(p.score.total);
              return (
                <button key={p.barcode} onClick={() => navigate(`/product/${p.barcode}`)} data-testid={`loaded-${p.barcode}`}
                  className="bg-card rounded-2xl border border-border overflow-hidden text-left shadow-[0_2px_8px_rgb(0,0,0,0.04)]">
                  <div className="aspect-square bg-slate-50 flex items-center justify-center overflow-hidden">
                    {p.image_url ? <img src={resolveImage(p.image_url)} alt="" className="w-full h-full object-contain p-2" /> : <ImageOff className="w-8 h-8 text-slate-300" />}
                  </div>
                  <div className="p-3">
                    <p className="font-semibold text-sm truncate">{p.name}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="w-2 h-2 rounded-full" style={{ background: gg.solid }} />
                      <span className="text-xs font-medium" style={{ color: gg.text }}>{p.score.total} · {t(`grade.${gg.key}`)}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

const F = ({ label, children }) => (<div><Label className="mb-1.5 block">{label}</Label>{children}</div>);
